import { ImageSourcePropType } from 'react-native';

import {
	CrashJpg,
	CrossyRoadJpg,
	HigherLowerJpg,
	LuckyWheelJpg,
	PenaltyShootoutsJpg,
	PlinkoJpg,
	RedOrBlackPng,
	ScratchPng,
	SlotPng,
	TreasureChestsJpg,
} from '../../../assets/bonuses';
import i18n from '../../../i18n/i18n';
import type { ArcadeGameType } from '../../../services/arcadeApi';

/**
 * The games the Figma frame "16/10 VIP / bonuses / arcade games" (7019:79805) lays out: three
 * featured cards across the top and the VIP Arcade grid under the crown heading - four games, then
 * three marked "Coming soon". The four VIP games carry `arcadeGameType`: the card's PLAY opens
 * that Pixi pack in `ArcadeGameModal`.
 *
 * Titles are getters, so they resolve through i18n when the card reads them, never at module
 * load - translations arrive from the backend after start-up.
 */
export type ArcadeGame = {
	key: string;
	/** Translated on read. The compact card wraps it over its two lines where it does not fit. */
	title: string;
	artwork: ImageSourcePropType;
	/** The design ribbons the art "COMING SOON" and greys the PLAY button. */
	comingSoon?: boolean;
	/** Set only for the games that exist: the path the mini-game host loads. */
	launchPath?: string;
	/** Hours before the free play comes back round. */
	cooldownHours?: number;
	/** Key into `state.miniGames.games`. */
	miniGameKey?: 'black_and_red' | 'scratch_card' | 'slot';
	/**
	 * Set for the VIP Arcade games (Pixi packs hosted by `ArcadeGameModal`); the card's PLAY opens
	 * that game. Without it the card is a "coming soon" placeholder.
	 */
	arcadeGameType?: ArcadeGameType;
};

export const FEATURED_GAMES: ArcadeGame[] = [
	{
		key: 'black_and_red',
		get title() {
			return i18n.t('T_SITE_MODAL_BONUS_RED_BLACK_TITLE', { defaultValue: 'RED OR BLACK' });
		},
		artwork: RedOrBlackPng,
		launchPath: '/blackAndRed/bowaPlayer.html',
		cooldownHours: 4,
		miniGameKey: 'black_and_red',
	},
	{
		key: 'scratch',
		get title() {
			return i18n.t('T_SITE_MODAL_BONUS_SCRATCH_TITLE', { defaultValue: 'SCRATCH' });
		},
		artwork: ScratchPng,
		launchPath: '/scratch/bowaPlayer.html',
		cooldownHours: 8,
		miniGameKey: 'scratch_card',
	},
	{
		key: 'slot',
		get title() {
			return i18n.t('T_SITE_MODAL_BONUS_SLOT_TITLE', { defaultValue: 'SLOT' });
		},
		artwork: SlotPng,
		launchPath: '/slot/bowaPlayer.html',
		cooldownHours: 12,
		miniGameKey: 'slot',
	},
];

export const VIP_GAMES: ArcadeGame[] = [
	{
		key: 'higher_lower',
		get title() {
			return i18n.t('T_SITE_BONUS_GAME_HIGHER_LOWER', { defaultValue: 'HIGHER\nLOWER' });
		},
		artwork: HigherLowerJpg,
		arcadeGameType: 'higher_lower',
	},
	{
		key: 'lucky_wheel',
		get title() {
			return i18n.t('T_SITE_BONUS_GAME_LUCKY_WHEEL', { defaultValue: 'LUCKY\nWHEEL' });
		},
		artwork: LuckyWheelJpg,
		arcadeGameType: 'lucky_wheel',
	},
	{
		key: 'plinko',
		get title() {
			return i18n.t('T_SITE_BONUS_GAME_PLINKO', { defaultValue: 'PLINKO' });
		},
		artwork: PlinkoJpg,
		arcadeGameType: 'plinko',
	},
	{
		key: 'treasure_chests',
		get title() {
			return i18n.t('T_SITE_BONUS_GAME_TREASURE_CHESTS', { defaultValue: 'TREASURE\nCHESTS' });
		},
		artwork: TreasureChestsJpg,
		arcadeGameType: 'treasure_chests',
	},
];

export const VIP_GAMES_SECOND_ROW: ArcadeGame[] = [
	{
		key: 'penalty_shootouts',
		get title() {
			return i18n.t('T_SITE_BONUS_GAME_PENALTY_SHOOTOUTS', { defaultValue: 'PENALTY\nSHOOTOUTS' });
		},
		artwork: PenaltyShootoutsJpg,
		comingSoon: true,
	},
	{
		key: 'crash',
		get title() {
			return i18n.t('T_SITE_BONUS_GAME_CRASH', { defaultValue: 'CRASH' });
		},
		artwork: CrashJpg,
		comingSoon: true,
	},
	{
		key: 'crossy_road',
		get title() {
			return i18n.t('T_SITE_BONUS_GAME_CROSSY_ROAD', { defaultValue: 'CROSSY\nROAD' });
		},
		artwork: CrossyRoadJpg,
		comingSoon: true,
	},
];
